export type PerformanceTier = 0 | 1 | 2 | 3;

export interface PerformanceSample {
  readonly fps: number;
  readonly tier: PerformanceTier;
  readonly tierChanged: boolean;
  readonly reason: "stable" | "below_target" | "degraded" | "recovered";
}

const DOWNGRADE_THRESHOLDS: Readonly<Record<PerformanceTier, number>> = {
  0: 68,
  1: 58,
  2: 48,
  3: 0,
};

export class FramePerformanceMonitor {
  private elapsedMs = 0;
  private frames = 0;
  private lowWindows = 0;
  private recoveryWindows = 0;
  private tier: PerformanceTier = 0;

  constructor(
    private readonly sampleWindowMs = 1000,
    private readonly targetFps = 72,
  ) {}

  recordFrame(deltaMs: number): PerformanceSample | null {
    if (!Number.isFinite(deltaMs) || deltaMs <= 0 || deltaMs > 1000) return null;
    this.elapsedMs += deltaMs;
    this.frames += 1;
    if (this.elapsedMs + 0.01 < this.sampleWindowMs) return null;

    const fps = (this.frames * 1000) / this.elapsedMs;
    this.elapsedMs = 0;
    this.frames = 0;
    const threshold = DOWNGRADE_THRESHOLDS[this.tier];
    this.lowWindows = fps < threshold ? this.lowWindows + 1 : 0;
    this.recoveryWindows = fps >= this.targetFps - 0.5 ? this.recoveryWindows + 1 : 0;

    if (this.tier < 3 && this.lowWindows >= 3) {
      this.tier = (this.tier + 1) as PerformanceTier;
      this.lowWindows = 0;
      this.recoveryWindows = 0;
      return { fps, tier: this.tier, tierChanged: true, reason: "degraded" };
    }
    if (this.tier > 0 && this.recoveryWindows >= 8) {
      this.tier = (this.tier - 1) as PerformanceTier;
      this.lowWindows = 0;
      this.recoveryWindows = 0;
      return { fps, tier: this.tier, tierChanged: true, reason: "recovered" };
    }
    return {
      fps,
      tier: this.tier,
      tierChanged: false,
      reason: fps < this.targetFps - 0.5 ? "below_target" : "stable",
    };
  }

  getTier(): PerformanceTier {
    return this.tier;
  }
}
